import { PageHeader } from "@bednbody/ui/components/page-header";
import { Callout } from "@bednbody/ui/components/callout";
import { Button, buttonVariants } from "@bednbody/ui/components/button";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { cn } from "@bednbody/ui/lib/utils";
import { ArrowSquareOutIcon, WarningIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  type DistanceBand,
  type OperatingSettings,
  useDistanceBands,
  useOperatingSettings,
  useUpdateDistanceBands,
  useUpdateOperatingSettings,
} from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";
import { CancellationFeeCard } from "./CancellationFeeCard";
import { CashHandoverCard } from "./CashHandoverCard";
import { PickupRangeCard } from "./PickupRangeCard";
import { TherapistLocationWindowCard } from "./TherapistLocationWindowCard";
import { ServiceAreaCard } from "./ServiceAreaCard";
import { WaitlistCard } from "./WaitlistCard";

/**
 * The numbers Luigi asked to be editable (2026-09-11), in one superadmin screen.
 *
 * Every number here is live, for new work only: the booking server prices from
 * the distance bands, the rider-sessions function staffs from the ratio and pays
 * from the rider rate, and the database's securing gate reads the minimums
 * (0217). The banner says when each one takes effect, because an edit never
 * rewrites a booking, a secured week or a settled night.
 *
 * ⚠ A BAND IS EDITED BY ITS UPPER BOUND ONLY. Each row's lower bound is the
 * previous row's upper bound, rendered as text. That is deliberate: the live
 * overcharge this table replaces existed because the bands were written as
 * separate buckets with gaps between them (8-9, then 10-11), so a client at
 * 9.5 km fell through to the next tier and paid 15% where DIST-005 says 10%.
 * Upper-bound-only editing cannot express a gap.
 */
export function SettingsPage() {
  const settings = useOperatingSettings();
  const bands = useDistanceBands();

  const loading = settings.isLoading || bands.isLoading;
  const failed = settings.isError || bands.isError;

  return (
    <div className="flex min-w-0 flex-col gap-8">
      <PageHeader
        description="App-wide operating numbers. Superadmin only, and every change is recorded in the audit log."
        title="Settings"
      />

      <Callout title="How changes take effect" tone="warning" icon={WarningIcon}>
        Every setting here is live, for new work only. Distance bands price new bookings; a client
        with the app already open may see the old surcharge for up to 30 minutes. Weekly minimums
        (now under Rankings) apply to weeks secured after you save. Riders per therapist applies at
        the next Saturday rider-session run. The rider rate applies to sessions paid after you save.
      </Callout>

      {failed ? (
        <Callout tone="danger" role="alert" title="Settings could not be loaded">
          <div className="flex flex-col gap-3">
            <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
              These settings could not be loaded. That is a failed read, not an empty setting.
            </p>
            <RetryButton
              size="sm"
              onRetry={() => Promise.all([settings.refetch(), bands.refetch()])}
            />
          </div>
        </Callout>
      ) : null}

      {loading ? (
        <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
          Loading settings…
        </p>
      ) : null}

      {!loading && !failed && settings.data ? (
        <>
          <BookingHoursCard settings={settings.data} />
          <WaitlistCard settings={settings.data} />
          <PickupRangeCard settings={settings.data} />
          <TherapistLocationWindowCard settings={settings.data} />
          <CashHandoverCard settings={settings.data} />
          <CancellationFeeCard settings={settings.data} />
          <SessionsCard settings={settings.data} />
          <RiderRateCard settings={settings.data} />
          <ServiceAreaCard settings={settings.data} bands={bands.data ?? []} />
          <DistanceCard bands={bands.data ?? []} settings={settings.data} />
          <CatalogCard />
        </>
      ) : null}
    </div>
  );
}

/* ── sessions ───────────────────────────────────────────────────────────── */

function SessionsCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [ratio, setRatio] = useState(() => String(settings.therapists_per_rider));

  useEffect(() => {
    setRatio(String(settings.therapists_per_rider));
  }, [settings]);

  function save() {
    const value = Number(ratio);
    if (!Number.isInteger(value) || value < 1) {
      toast.error("Therapists per rider has to be a whole number, 1 or more.");
      return;
    }
    update.mutate(
      { therapists_per_rider: value },
      {
        onSuccess: () => toast.success("Session settings saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader>
        <CardTitle className="type-title-body">
          <h2>Sessions</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          How many therapists one rider covers. The weekly days each rank must secure moved to{" "}
          <Link to="/rankings" className="text-primary underline-offset-4 hover:underline">
            Rankings
          </Link>
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label="Riders needed — one rider per how many therapists">
          <NumberInput value={ratio} onChange={setRatio} />
        </Field>
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}

/* ── booking hours ──────────────────────────────────────────────────────── */

/** "14" -> "2 PM". The window always ends at midnight (0243). */
function hourLabel(hour: number): string {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return "—";
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12} ${period}`;
}

function BookingHoursCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState(() => String(settings.booking_start_hour));

  useEffect(() => {
    setDraft(String(settings.booking_start_hour));
  }, [settings]);

  const parsed = Number(draft);
  const valid = Number.isInteger(parsed) && parsed >= 0 && parsed <= 23;

  function save() {
    if (!valid) {
      toast.error("The first bookable hour has to be a whole number, 0 to 23.");
      return;
    }
    update.mutate(
      { booking_start_hour: parsed },
      {
        onSuccess: () => toast.success("Booking hours saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader>
        <CardTitle className="type-title-subsection!">Booking hours</CardTitle>
        <CardDescription className="max-w-prose type-body-small">
          The first hour clients can book &mdash; for Scheduled slots and for Book Now (ASAP). The
          last slot always starts at 11 PM and Book Now closes at 11:59 PM &mdash; only the start
          moves.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label="First bookable hour (0&ndash;23, Manila)">
          <NumberInput value={draft} onChange={setDraft} className="w-28" />
        </Field>
        <p className="max-w-prose text-sm font-medium text-foreground">
          {valid
            ? `Clients will see ${24 - parsed} slots, ${hourLabel(parsed)} to 12 AM, and Book Now opens at ${hourLabel(parsed)}.`
            : "Enter a whole number from 0 to 23."}
        </p>
        {/* Not a warning about this screen — a warning about what it cannot do.
            Duty is what makes a window bookable, so lowering this without a
            matching schedule just produces more Fully Booked slots. */}
        <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
          This only opens the window. Therapists and riders still have to be on duty for those
          hours, or the new slots will show as Fully Booked. Book Now also needs a therapist whose
          Session has started &mdash; Sessions run 2 PM to 12 AM, so before 2 PM it finds nobody
          unless her Session was moved earlier.
        </p>
        {parsed !== 14 && valid ? (
          <p className="max-w-prose text-sm leading-relaxed text-warning">
            2 PM is the approved start. {hourLabel(parsed)} is a deliberate exception &mdash; set it
            back to 14 when the demo or pilot is over.
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}

/* ── distance and surcharge ─────────────────────────────────────────────── */

interface BandDraft {
  id: string;
  maxKm: string;
  client: string;
  rider: string;
  therapist: string;
  solo: string;
  perClient: string;
}

const toDraft = (band: DistanceBand): BandDraft => ({
  id: band.id,
  maxKm: String(band.max_km),
  client: String(band.client_rate * 100),
  rider: String(band.rider_rate * 100),
  therapist: String(band.therapist_rate * 100),
  solo: String(band.min_price_solo_php),
  perClient: String(band.min_price_per_client_php),
});

function DistanceCard({
  bands,
  settings,
}: {
  bands: readonly DistanceBand[];
  settings: OperatingSettings;
}) {
  const updateBands = useUpdateDistanceBands();
  const [draft, setDraft] = useState<BandDraft[]>(() => bands.map(toDraft));

  useEffect(() => setDraft(bands.map(toDraft)), [bands]);

  function patch(id: string, change: Partial<BandDraft>) {
    setDraft((rows) => rows.map((row) => (row.id === id ? { ...row, ...change } : row)));
  }

  function save() {
    // A band starts where the previous one ended — carried forward rather than
    // indexed back, so the lower bounds cannot disagree with the upper ones.
    let previousMax = 0;
    const rows = draft.map((row, index) => {
      const minKm = previousMax;
      // The last band ends at the service area. The server refuses any schedule
      // that does not (DIST-009), so the screen does not offer to change it.
      const maxKm = index === draft.length - 1 ? settings.service_area_limit_km : Number(row.maxKm);
      previousMax = maxKm;
      return {
        id: row.id,
        minKm,
        maxKm,
        client: Number(row.client),
        rider: Number(row.rider),
        therapist: Number(row.therapist),
        solo: Number(row.solo),
        perClient: Number(row.perClient),
      };
    });

    const numbers = rows.flatMap((r) => [
      r.maxKm,
      r.client,
      r.rider,
      r.therapist,
      r.solo,
      r.perClient,
    ]);
    if (numbers.some((n) => !Number.isFinite(n) || n < 0)) {
      toast.error("Every distance, percentage and minimum has to be a number, 0 or more.");
      return;
    }
    if (rows.some((r) => r.maxKm <= r.minKm)) {
      toast.error("Each band has to end further out than the one before it.");
      return;
    }
    if (rows.some((r) => r.client > 100 || r.rider > 100 || r.therapist > 100)) {
      toast.error("A percentage cannot be above 100.");
      return;
    }

    const edits = rows.map((r) => ({
      id: r.id,
      patch: {
        min_km: r.minKm,
        max_km: r.maxKm,
        client_rate: r.client / 100,
        rider_rate: r.rider / 100,
        therapist_rate: r.therapist / 100,
        min_price_solo_php: r.solo,
        min_price_per_client_php: r.perClient,
      },
    }));

    updateBands.mutate(edits, {
      onError: (error) => toast.error(error.message),
      onSuccess: () => toast.success("Distance bands saved."),
    });
  }

  const columns = useMemo<ColumnDef<BandDraft, unknown>[]>(
    () => [
      {
        id: "band",
        accessorFn: (row) => {
          const index = draft.findIndex((candidate) => candidate.id === row.id);
          return index === 0 ? 0 : Number(draft[index - 1]?.maxKm ?? 0);
        },
        header: "Band (km)",
        cell: ({ row }) => {
          const index = draft.findIndex((candidate) => candidate.id === row.original.id);
          const from = index === 0 ? "0" : (draft[index - 1]?.maxKm ?? "0");
          return (
            <AdminTableIdentity
              title={`${from} – ${index === draft.length - 1 ? settings.service_area_limit_km : row.original.maxKm} km`}
              subtitle="Distance pricing band"
              fallback={index + 1}
            />
          );
        },
      },
      {
        id: "upperBound",
        accessorFn: (row) => Number(row.maxKm),
        header: "Upper bound",
        cell: ({ row }) => {
          const index = draft.findIndex((candidate) => candidate.id === row.original.id);
          return index === draft.length - 1 ? (
            <span className="tabular-nums">{settings.service_area_limit_km} km</span>
          ) : (
            <NumberInput
              className="w-20"
              step="0.5"
              value={row.original.maxKm}
              onChange={(value) => patch(row.original.id, { maxKm: value })}
            />
          );
        },
      },
      ...(
        [
          ["client", "Client %"],
          ["rider", "Rider %"],
          ["therapist", "Therapist %"],
          ["solo", "Min ₱ (1 client)"],
          ["perClient", "Min ₱ (2–4)"],
        ] as const
      ).map(
        ([key, label]): ColumnDef<BandDraft, unknown> => ({
          id: key,
          accessorFn: (row) => Number(row[key]),
          header: label,
          cell: ({ row }) => (
            <NumberInput
              className={key === "solo" || key === "perClient" ? "w-24" : "w-20"}
              step={key === "client" || key === "rider" || key === "therapist" ? "0.5" : undefined}
              value={row.original[key]}
              onChange={(value) => patch(row.original.id, { [key]: value })}
            />
          ),
        }),
      ),
      {
        id: "actions",
        enableSorting: false,
        header: "",
        cell: ({ row }) => (
          <AdminTableRowMenu
            label="distance band"
            actions={[
              {
                label: "Reset this band",
                onSelect: () => {
                  const original = bands.find((band) => band.id === row.original.id);
                  if (original) patch(row.original.id, toDraft(original));
                },
              },
            ]}
          />
        ),
      },
    ],
    [bands, draft, settings.service_area_limit_km],
  );

  return (
    <Card className="gap-5">
      <CardHeader>
        <CardTitle className="type-title-body">
          <h2>Distance and surcharge</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          Each band starts where the previous one ends. The final band always reaches the service
          area, set in the card above.
        </CardDescription>
      </CardHeader>
      <CardContent className="min-w-0">
        <AdminDataTable
          presentation="embedded"
          data={draft}
          columns={columns}
          getRowId={(row) => row.id}
          searchText={(row) =>
            `distance band ${row.maxKm} ${row.client} ${row.rider} ${row.therapist}`
          }
          searchPlaceholder="Search distance bands…"
          exportFilename="distance-surcharge-settings"
          exportColumns={[
            { header: "Band ID", value: (row) => row.id },
            { header: "Upper bound km", value: (row) => row.maxKm },
            { header: "Client %", value: (row) => row.client },
            { header: "Rider %", value: (row) => row.rider },
            { header: "Therapist %", value: (row) => row.therapist },
            { header: "Minimum ₱ (1 client)", value: (row) => row.solo },
            { header: "Minimum ₱ (2–4)", value: (row) => row.perClient },
          ]}
          emptyTitle="No distance bands"
          emptyDescription="Distance pricing cannot be edited until the configured bands load."
        />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={updateBands.isPending}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}

/* ── rider rate ─────────────────────────────────────────────────────────── */

function RiderRateCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState(() => ({
    fare: String(settings.rider_base_fare_php),
    covers: String(settings.rider_base_covers_km),
    perKm: String(settings.rider_per_km_php),
  }));

  useEffect(() => {
    setDraft({
      fare: String(settings.rider_base_fare_php),
      covers: String(settings.rider_base_covers_km),
      perKm: String(settings.rider_per_km_php),
    });
  }, [settings]);

  function save() {
    const values = {
      rider_base_fare_php: Number(draft.fare),
      rider_base_covers_km: Number(draft.covers),
      rider_per_km_php: Number(draft.perKm),
    };
    if (Object.values(values).some((n) => !Number.isFinite(n) || n < 0)) {
      toast.error("A rate has to be a number, 0 or more.");
      return;
    }
    update.mutate(values, {
      onSuccess: () => toast.success("Rider rate saved."),
      onError: (error) => toast.error(error.message),
    });
  }

  return (
    <Card className="gap-5">
      <CardHeader>
        <CardTitle className="type-title-body">
          <h2>Rider rate</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          A flat fare covering the first few kilometres, then a per-kilometre rate. Riders already
          paid keep the rate they were paid at.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Flat fare (₱)">
          <NumberInput value={draft.fare} onChange={(v) => setDraft((d) => ({ ...d, fare: v }))} />
        </Field>
        <Field label="Covers first (km)">
          <NumberInput
            step="0.5"
            value={draft.covers}
            onChange={(v) => setDraft((d) => ({ ...d, covers: v }))}
          />
        </Field>
        <Field label="Then per km (₱)">
          <NumberInput
            step="0.5"
            value={draft.perKm}
            onChange={(v) => setDraft((d) => ({ ...d, perKm: v }))}
          />
        </Field>
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}

/* ── catalog ────────────────────────────────────────────────────────────── */

function CatalogCard() {
  return (
    <Card className="gap-5">
      <CardHeader>
        <CardTitle className="type-title-body">
          <h2>Service catalog</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          Prices, durations and photos are edited on the Catalog page.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-3">
        <Link
          to="/catalog"
          className={cn(buttonVariants({ variant: "outline", size: "sm" }), "w-fit")}
        >
          Open Catalog <ArrowSquareOutIcon className="size-4" />
        </Link>
        <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
          Open a service there to change its photo.
        </p>
      </CardContent>
    </Card>
  );
}

/* ── small shared pieces ────────────────────────────────────────────────── */
